import axios from "axios";
import { BASE_URL } from "../config";

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("authentication/token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Settlement List ──────────────────────────────────────────────────────────

export const getSettlementList = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement`, {
    params,
    headers: getAuthHeaders(),
  });

export const exportSettlementListCsv = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/csv`, {
    params,
    headers: getAuthHeaders(),
    responseType: "blob",
  });

// ─── Settlement History ───────────────────────────────────────────────────────

export const getSettlementHistory = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/history`, {
    params,
    headers: getAuthHeaders(),
  });

export const exportSettlementHistoryCsv = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/history/csv`, {
    params,
    headers: getAuthHeaders(),
    responseType: "blob",
  });

// ─── Settlement Actions ───────────────────────────────────────────────────────

/**
 * Save cheque / provider OR details for a settlement row.
 * Converts SP-WP-update_settlement_details
 */
export const saveSettlementChequeDetails = (data) =>
  axios.post(`${BASE_URL}/provider-payment/settlement/cheque-details`, data, {
    headers: getAuthHeaders(),
  });

/**
 * Tag actual date deducted for a payment reference number.
 * Converts SP-WP-tag_date_deducted
 */
export const tagSettlementDateDeducted = (data) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/tag-date-deducted`,
    data,
    { headers: getAuthHeaders() }
  );

/**
 * Load saved/default check status details for a settlement row.
 */
export const getSettlementCheckStatus = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/check-status`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Save check status tagging data for a settlement row.
 */
export const saveSettlementCheckStatus = (data) =>
  axios.post(`${BASE_URL}/provider-payment/settlement/check-status`, data, {
    headers: getAuthHeaders(),
  });

/**
 * Upload an attachment (deposit_slip or or_amap) for a settlement row.
 * Converts SP-WP-add_settlement_attachment + settlement_attachments.php
 * @param {FormData} formData - must contain: tbl_id, type, file, [amap_or_no, amap_or_date, amap_or_amount]
 */
export const addSettlementAttachment = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/attachment`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Get records for batch CV print.
 * Converts SP-WP-get_for_batch_print
 */
export const getSettlementBatchPrintData = (startCv, endCv) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/batch-print`, {
    params: { start_cv: startCv, end_cv: endCv },
    headers: getAuthHeaders(),
  });

/**
 * Build HTML payload for batch Check Voucher print based on consolidated CV numbers.
 * @param {string|string[]} cvNos - comma-separated CV numbers
 * @param {string|string[]} tblIds - optional comma-separated selected sr.tbl_id values
 */
export const getSettlementBatchPrintCvHtml = (cvNos, tblIds = "") => {
  const params = {};
  const normalizedCvNos = Array.isArray(cvNos) ? cvNos.join(",") : cvNos;
  const normalizedTblIds = Array.isArray(tblIds) ? tblIds.join(",") : tblIds;

  if (normalizedCvNos) params.cv_nos = normalizedCvNos;
  if (normalizedTblIds) params.tbl_ids = normalizedTblIds;

  return axios.get(`${BASE_URL}/provider-payment/settlement/batch-print-cv`, {
    params,
    headers: getAuthHeaders(),
  });
};

/**
 * Get provider delivery address labels (v1 parity).
 * Optional filter: provider_codes (comma/newline separated string, max 500 unique codes).
 */
export const getSettlementDeliveryAddressLabels = (params = {}) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/delivery-address-labels`, {
    params,
    headers: getAuthHeaders(),
  });

// ─── Settlement Uploads ───────────────────────────────────────────────────────

/**
 * Upload / change Form 2307 file for a specific settlement row.
 * @param {FormData} formData - must contain: tbl_id, file
 */
export const uploadSettlementForm2307 = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/form-2307`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Upload Provider OR file for a specific settlement row.
 * @param {FormData} formData - must contain: tbl_id, file
 */
export const uploadSettlementProviderOr = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/provider-or`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Batch Settlement CSV Upload.
 * @param {FormData} formData - must contain: file (csv/txt)
 */
export const uploadSettlementBatchSr = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/batch-sr`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Batch Regenerate PIS/Form 2307 upload.
 * @param {FormData} formData - must contain: file (csv/txt/xlsx), optional source_original_file_name
 */
export const uploadSettlementPdfRegenerationBatch = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/pdf-regeneration-batch/upload`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Manual Batch Regenerate PIS/Form 2307.
 * @param {Object} payload - { rows: [{ payment_ref_no, resend_email_notifications }] }
 */
export const submitSettlementPdfRegenerationManualBatch = (payload) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/pdf-regeneration-batch/manual`,
    payload,
    {
      headers: getAuthHeaders(),
    }
  );

/**
 * Batch Deposit Slip / OR multi-file upload.
 * Files must be named TYPE_PRN.ext (e.g. DS_P-1234.png)
 * @param {FormData} formData - must contain: files[] (pdf/jpg/png, max 200)
 */
export const uploadSettlementBatchDepoOr = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/batch-depo-or`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Get Deposit Slip / OR files batch upload history.
 * @param {Object} params - { page, limit, search, status, uploader }
 */
export const getDepoOrBatchUploadHistory = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/upload/depo-or-batch-history`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Get processed rows (success + failed) for a specific Deposit Slip / OR files batch upload.
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { page, limit, type: 'all'|'success'|'failed' }
 */
export const getDepoOrBatchProcessedRows = (uploadId, params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/upload/depo-or-batch-processed/${uploadId}`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Export Deposit Slip / OR files processed rows as CSV (success + failed).
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { type: 'all'|'success'|'failed' }
 */
export const exportDepoOrBatchProcessedRows = (uploadId, params) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/depo-or-batch-processed-export/${uploadId}`,
    {},
    {
      params,
      headers: getAuthHeaders(),
      responseType: "blob",
    }
  );

/**
 * Batch SR Depo CSV Upload.
 * @param {FormData} formData - must contain: file (csv/txt)
 */
export const uploadSettlementBatchSrDepo = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/batch-sr-depo`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Batch Date Deducted CSV Upload.
 * @param {FormData} formData - must contain: file (csv/txt)
 */
export const uploadSettlementBatchDateDeducted = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/batch-date-deducted`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Check Status Report CSV export.
 * @param {Object} params - { date_from, date_to, new_date_from, new_date_to }
 */
export const exportSettlementCheckStatusReport = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/check-status/report`, {
    params,
    headers: getAuthHeaders(),
    responseType: "blob",
  });

/**
 * Batch Check Status CSV Upload.
 * @param {FormData} formData - must contain: file (csv/txt)
 */
export const uploadCheckStatusBatch = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/check-status-batch/upload`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Get Check Status batch upload history.
 * @param {Object} params - { page, limit, search, status, uploader }
 */
export const getCheckStatusBatchUploadHistory = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/check-status-batch/history`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Get Check Status batch processed rows.
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { page, limit, type: 'all'|'success'|'failed' }
 */
export const getCheckStatusBatchProcessedRows = (uploadId, params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/check-status-batch/processed/${uploadId}`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Export Check Status batch processed rows.
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { type: 'all'|'success'|'failed' }
 */
export const exportCheckStatusBatchProcessedRows = (uploadId, params) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/check-status-batch/processed-export/${uploadId}`,
    {},
    {
      params,
      headers: getAuthHeaders(),
      responseType: "blob",
    }
  );

/**
 * Refresh Check Status batch source CSV signed URL.
 * @param {string} uploadId - UUID of upload batch
 */
export const refreshCheckStatusBatchUploadSignedUrl = (uploadId) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/check-status-batch/refresh-url`,
    { upload_id: uploadId },
    { headers: getAuthHeaders() }
  );

// ─── Settlement Batch Upload (New Async Upload System) ──────────────────────

/**
 * Get batch upload history with pagination/search/filter.
 * @param {Object} params - { page, limit, search, status }
 */
export const getSettlementBatchUploadHistory = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/upload/batch-history`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Get processed rows (success + failed) for a specific upload batch.
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { page, limit, type: 'all'|'success'|'failed' }
 */
export const getSettlementBatchProcessedRows = (uploadId, params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/upload/batch-processed/${uploadId}`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Get generated settlement PDF files for a specific upload batch.
 * Includes token-backed public share links.
 * @param {string} uploadId - UUID of upload batch
 */
export const getSettlementBatchGeneratedFiles = (uploadId) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/upload/batch-generated-files/${uploadId}`, {
    headers: getAuthHeaders(),
  });

/**
 * Export processed rows as CSV (success + failed).
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { type: 'all'|'success'|'failed' }
 */
export const exportSettlementBatchProcessedRows = (uploadId, params) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/batch-processed-export/${uploadId}`,
    {},
    {
      params,
      headers: getAuthHeaders(),
      responseType: "blob",
    }
  );

/**
 * Refresh an expired signed URL for a batch upload CSV.
 * @param {string} uploadId - UUID of the upload batch
 */
export const refreshSettlementBatchUploadSignedUrl = (uploadId) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/batch-refresh-url`,
    { upload_id: uploadId },
    { headers: getAuthHeaders() }
  );

/**
 * Get Batch Regenerate PIS/Form 2307 markdown specs.
 */
export const getSettlementPdfRegenerationBatchSpecs = () =>
  axios.get(`${BASE_URL}/provider-payment/settlement/pdf-regeneration-batch/specs`, {
    headers: getAuthHeaders(),
  });

/**
 * Get Batch Regenerate PIS/Form 2307 upload history.
 * @param {Object} params - { page, limit, search, status, uploader }
 */
export const getSettlementPdfRegenerationBatchHistory = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/pdf-regeneration-batch/history`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Get processed rows for Batch Regenerate PIS/Form 2307.
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { page, limit, type: 'all'|'success'|'failed' }
 */
export const getSettlementPdfRegenerationBatchProcessedRows = (uploadId, params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/pdf-regeneration-batch/processed/${uploadId}`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Export Batch Regenerate PIS/Form 2307 processed rows.
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { type: 'all'|'success'|'failed' }
 */
export const exportSettlementPdfRegenerationBatchProcessedRows = (uploadId, params) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/pdf-regeneration-batch/processed-export/${uploadId}`,
    {},
    {
      params,
      headers: getAuthHeaders(),
      responseType: "blob",
    }
  );

/**
 * Get generated PDF files for Batch Regenerate PIS/Form 2307.
 * @param {string} uploadId - UUID of upload batch
 */
export const getSettlementPdfRegenerationBatchGeneratedFiles = (uploadId) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/pdf-regeneration-batch/generated-files/${uploadId}`, {
    headers: getAuthHeaders(),
  });

/**
 * Refresh Batch Regenerate PIS/Form 2307 source payload signed URL.
 * @param {string} uploadId - UUID of upload batch
 */
export const refreshSettlementPdfRegenerationBatchSignedUrl = (uploadId) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/pdf-regeneration-batch/refresh-url`,
    { upload_id: uploadId },
    { headers: getAuthHeaders() }
  );

// ─── Deposit Slip Batch Upload (Async Upload System) ─────────────────────────

/**
 * Get Deposit Slip batch upload history with pagination/search/filter.
 * @param {Object} params - { page, limit, search, status, uploader }
 */
export const getDepositSlipBatchUploadHistory = (params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/upload/deposit-slip-batch-history`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Get processed rows (success + failed) for a specific Deposit Slip upload batch.
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { page, limit, type: 'all'|'success'|'failed' }
 */
export const getDepositSlipBatchProcessedRows = (uploadId, params) =>
  axios.get(`${BASE_URL}/provider-payment/settlement/upload/deposit-slip-batch-processed/${uploadId}`, {
    params,
    headers: getAuthHeaders(),
  });

/**
 * Export Deposit Slip processed rows as CSV (success + failed).
 * @param {string} uploadId - UUID of upload batch
 * @param {Object} params - { type: 'all'|'success'|'failed' }
 */
export const exportDepositSlipBatchProcessedRows = (uploadId, params) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/deposit-slip-batch-processed-export/${uploadId}`,
    {},
    {
      params,
      headers: getAuthHeaders(),
      responseType: "blob",
    }
  );

/**
 * Refresh an expired signed URL for a Deposit Slip batch upload CSV.
 * @param {string} uploadId - UUID of the upload batch
 */
export const refreshDepositSlipBatchUploadSignedUrl = (uploadId) =>
  axios.post(
    `${BASE_URL}/provider-payment/settlement/upload/deposit-slip-batch-refresh-url`,
    { upload_id: uploadId },
    { headers: getAuthHeaders() }
  );

/**
 * Get or generate AI-powered summary for a Deposit Slip batch upload.
 * @param {string} uploadId - UUID of the upload batch
 */
export const getDepositSlipBatchAiSummary = (uploadId) =>
  axios.get(
    `${BASE_URL}/provider-payment/settlement/upload/deposit-slip-batch-ai-summary/${uploadId}`,
    { headers: getAuthHeaders() }
  );

/**
 * Get or generate AI-powered summary for a batch upload.
 * Returns cached summary if exists, otherwise generates via OpenAI.
 * @param {string} uploadId - UUID of the upload batch
 */
export const getSettlementBatchAiSummary = (uploadId) =>
  axios.get(
    `${BASE_URL}/provider-payment/settlement/upload/batch-ai-summary/${uploadId}`,
    { headers: getAuthHeaders() }
  );

// Credit Advise Upload (Async Upload System)

export const uploadCreditAdviseBatch = (formData) =>
  axios.post(
    `${BASE_URL}/provider-payment/credit-advise/upload`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    }
  );

export const getCreditAdviseUploadHistory = (params) =>
  axios.get(`${BASE_URL}/provider-payment/credit-advise/history`, {
    params,
    headers: getAuthHeaders(),
  });

export const getCreditAdviseProcessedRows = (uploadId, params) =>
  axios.get(`${BASE_URL}/provider-payment/credit-advise/processed/${uploadId}`, {
    params,
    headers: getAuthHeaders(),
  });

export const exportCreditAdviseProcessedRows = (uploadId, params) =>
  axios.post(
    `${BASE_URL}/provider-payment/credit-advise/processed-export/${uploadId}`,
    {},
    {
      params,
      headers: getAuthHeaders(),
      responseType: "blob",
    }
  );

export const refreshCreditAdviseUploadSignedUrl = (uploadId) =>
  axios.post(
    `${BASE_URL}/provider-payment/credit-advise/refresh-url`,
    { upload_id: uploadId },
    { headers: getAuthHeaders() }
  );

// Credit File
export const getCreditFileList = (params) =>
  axios.get(`${BASE_URL}/provider-payment/credit-file`, {
    params,
    headers: getAuthHeaders(),
  });

export const getCreditFileAll = (params) =>
  axios.get(`${BASE_URL}/provider-payment/credit-file/all`, {
    params,
    headers: getAuthHeaders(),
  });

export const getCreditFileCorporates = () =>
  axios.get(`${BASE_URL}/provider-payment/credit-file/corporates`, {
    headers: getAuthHeaders(),
  });

export const insertCreditFileLog = (data) =>
  axios.post(`${BASE_URL}/provider-payment/credit-file/log`, data, {
    headers: getAuthHeaders(),
  });
